import {
  FaClock,
  FaCheckCircle,
  FaPlay,
  FaFlag,
  FaUsers,
  FaClipboardList,
} from "react-icons/fa";
import { TOURNAMENT_STEPS_ARRAY } from "../hooks/useTournamentSteps";
import {
  getPublicProgressSteps,
  isBeforeFirstSquad,
} from "../utility/tournamentProgress";

const PUBLIC_STEP_CHROME = {
  tournamentSetup: {
    icon: FaClock,
    color: "bg-gray-100 text-gray-800 border-gray-200",
  },
  registrationOpen: {
    icon: FaUsers,
    color: "bg-brand-cream text-brand-charcoal border-brand-charcoal/10",
  },
  tournamentActive: {
    icon: FaPlay,
    color: "bg-brand-cream text-brand-charcoal border-brand-charcoal/10",
  },
  scoreProcessing: {
    icon: FaClipboardList,
    color: "bg-yellow-100 text-yellow-800 border-yellow-200",
  },
  resultsAvailable: {
    icon: FaFlag,
    color: "bg-brand-charcoal text-brand-cream border-brand-charcoal",
  },
};

export const getCurrentTournamentStatus = async (
  status,
  canManage,
  loading = false,
  error = null
) => {
  if (loading) {
    return {
      status: "Loading...",
      icon: FaClock,
      color: "bg-gray-100 text-gray-800 border-gray-200",
      description: "Checking tournament status",
      nextStep: null,
    };
  }

  if (error) {
    return {
      status: "Error",
      icon: FaClock,
      color: "bg-red-100 text-red-800 border-red-200",
      description: "Unable to load status",
      nextStep: "Please refresh the page and try again.",
    };
  }

  if (!status) {
    return {
      status: "Loading...",
      icon: FaClock,
      color: "bg-gray-100 text-gray-800 border-gray-200",
      description: "Checking tournament status",
      nextStep: null,
    };
  }

  if (!canManage) {
    return getPublicStatus(status);
  }

  if (
    status.hasSquads &&
    !status.allTeamScoresComplete &&
    isBeforeFirstSquad(status)
  ) {
    return {
      status: "Scheduled",
      icon: FaClock,
      color: "bg-brand-cream text-brand-charcoal border-brand-charcoal/10",
      description: "Squads are set. Scoring starts on tournament day.",
      nextStep: null,
    };
  }

  for (const step of TOURNAMENT_STEPS_ARRAY) {
    if (!step.isComplete(status)) {
      return {
        status: step.inProgressLabel || step.label,
        icon: step.icon,
        color: getStatusColor(step.key),
        description: step.description,
        nextStep: step.action,
      };
    }
  }

  return {
    status: "Standings Finalized",
    icon: FaCheckCircle,
    color: "bg-brand-charcoal text-brand-cream border-brand-charcoal",
    description: "All milestones completed",
    nextStep: "Tournament is fully complete!",
  };
};

const getPublicStatus = (status) => {
  const publicSteps = getPublicProgressSteps(status);

  for (const step of publicSteps) {
    if (!step.isComplete) {
      const chrome = PUBLIC_STEP_CHROME[step.key] || PUBLIC_STEP_CHROME.tournamentSetup;
      return {
        status: step.inProgressLabel || step.label,
        icon: chrome.icon,
        color: chrome.color,
        description: step.description,
        nextStep: null,
      };
    }
  }

  return {
    status: "Standings Available",
    icon: FaFlag,
    color: "bg-brand-charcoal text-brand-cream border-brand-charcoal",
    description: "Final standings posted",
    nextStep: null,
  };
};

const getStatusColor = (key) => {
  if (key === "resultsFinalized") {
    return "bg-brand-charcoal text-brand-cream border-brand-charcoal";
  }

  return "bg-brand-cream text-brand-charcoal border-brand-charcoal/10";
};

export const getMilestoneDetails = () => {
  return [
    {
      key: "hasEvents",
      label: "Events Created",
      description: "Add the events this tournament will bowl",
      action: "Go to Setup",
    },
    {
      key: "allEventsHaveFees",
      label: "Event Fees Set",
      description: "Set fees for every event",
      action: "Go to Setup",
    },
    {
      key: "hasSquads",
      label: "Squads Created",
      description: "Set the house, dates, and times",
      action: "Go to Squads",
    },
    {
      key: "allTeamScoresComplete",
      label: "Scoring Complete",
      description: "Every squad has bowlers and every bowler has scores",
      action: "Go to Scoring",
    },
    {
      key: "averagesVerified",
      label: "Averages Verified",
      description: "Verify bowler averages and calculate handicaps",
      action: "Go to Averages",
    },
    {
      key: "teamsVerified",
      label: "Scores Verified",
      description: "Review and verify all team scores are correct",
      action: "Go to Scoring",
    },
    {
      key: "payoutFinalized",
      label: "Payouts Finalized",
      description: "Finalize prize money distribution",
      action: "Go to Payouts",
    },
    {
      key: "resultsFinalized",
      label: "Standings Finalized",
      description: "Publish final standings",
      action: "Go to Standings",
    },
  ];
};
